import { Card, Typography } from "@mui/material";
import { memo, useMemo } from "react";

interface OrderType {
  count: number;
}

interface DashboardData {
  all_group_order_type?: OrderType[];
  all_vip?: number;
}

interface TaskCreatorData {
  today?: DashboardData;
  today_qty?: DashboardData;
  all_time?: DashboardData;
  all_time_qty?: DashboardData;
  tomorrow?: DashboardData;
  tomorrow_qty?: DashboardData;
}

interface ComponentProps {
  isTask: boolean;
  titleOverview: string;
  data: TaskCreatorData;
}

// Shared card styles
const cardStyle = {
  border: "1px solid #ccc",
  borderRadius: "4px",
  padding: "16px",
};

const smallCardStyle = {
  border: "1px solid #ccc",
  borderRadius: "4px",
  padding: "8px",
};

// Helper functions for safe data access
const getOrderTypeCount = (
  data: DashboardData | undefined,
  index: number,
): number => {
  return data?.all_group_order_type?.[index]?.count || 0;
};

const getVipCount = (data: DashboardData | undefined): number => {
  return data?.all_vip || 0;
};

// Stat card component to reduce duplication
interface StatCardProps {
  label: string;
  value: number;
  textColor?: string;
  valueColor?: string;
  style?: Record<string, any>;
}

const StatCard = memo(
  ({
    label,
    value,
    textColor = "text-gray-400",
    valueColor = "text-black",
    style = smallCardStyle,
  }: StatCardProps) => (
    <div className="col-span-1" style={style}>
      <div className={`text-sm font-bold ${textColor}`}>{label}</div>
      <div className={`text-3xl font-bold ${valueColor}`}>{value}</div>
    </div>
  ),
);

StatCard.displayName = "StatCard";

const TaskCreator = memo(({ isTask, titleOverview, data }: ComponentProps) => {
  const calculatedData = useMemo(() => {
    const todayData = isTask ? data?.today : data?.today_qty;
    const allTimeData = isTask ? data?.all_time : data?.all_time_qty;
    const tomorrowData = isTask ? data?.tomorrow : data?.tomorrow_qty;

    // Today's counts
    const todayStock = getOrderTypeCount(todayData, 0);
    const todayRx = getOrderTypeCount(todayData, 1);
    const todayVip = getVipCount(todayData);

    // Tomorrow's counts
    const tomorrowStock = getOrderTypeCount(tomorrowData, 0);
    const tomorrowRx = getOrderTypeCount(tomorrowData, 1);
    const tomorrowVip = getVipCount(tomorrowData);

    // Yesterday's counts (all_time - today - tomorrow)
    const yesterdayStock = Math.max(
      0,
      getOrderTypeCount(allTimeData, 0) - todayStock - tomorrowStock,
    );
    const yesterdayRx = Math.max(
      0,
      getOrderTypeCount(allTimeData, 1) - todayRx - tomorrowRx,
    );
    const yesterdayVip = Math.max(
      0,
      getVipCount(allTimeData) - todayVip - tomorrowVip,
    );

    return {
      today: { stock: todayStock, rx: todayRx, vip: todayVip },
      yesterday: { stock: yesterdayStock, rx: yesterdayRx, vip: yesterdayVip },
      tomorrow: { stock: tomorrowStock, rx: tomorrowRx, vip: tomorrowVip },
    };
  }, [isTask, data]);

  return (
    <Card className="p-4 mb-4">
      <Typography variant="body1" className="font-semibold mb-2">
        {titleOverview} Creator
      </Typography>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* Today */}
        <div className="col-span-1" style={cardStyle}>
          <div className="flex flex-col gap-1">
            <div className="text-sm font-bold text-gray-400">CREATE TODAY</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <StatCard label="Stock" value={calculatedData.today.stock} />
              <StatCard label="RX" value={calculatedData.today.rx} />
              <StatCard
                label="VIP"
                value={calculatedData.today.vip}
                style={{ ...smallCardStyle, gridColumn: "span 2" }}
              />
            </div>
          </div>
        </div>

        {/* Yesterday */}
        <div className="col-span-1" style={cardStyle}>
          <div className="flex flex-col gap-1">
            <div className="text-sm font-bold text-error">CREATE YESTERDAY</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <StatCard
                label="Stock"
                value={calculatedData.yesterday.stock}
                textColor="text-error"
                valueColor="text-error-dark"
              />
              <StatCard
                label="RX"
                value={calculatedData.yesterday.rx}
                textColor="text-error"
                valueColor="text-error-dark"
              />
              <StatCard
                label="VIP"
                value={calculatedData.yesterday.vip}
                textColor="text-error"
                valueColor="text-error-dark"
                style={{ ...smallCardStyle, gridColumn: "span 2" }}
              />
            </div>
          </div>
        </div>

        {/* Tomorrow */}
        <div className="col-span-1" style={cardStyle}>
          <div className="flex flex-col gap-1">
            <div className="text-sm font-bold text-info">CREATE TOMORROW</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <StatCard
                label="Stock"
                value={calculatedData.tomorrow.stock}
                textColor="text-info"
                valueColor="text-info-dark"
              />
              <StatCard
                label="RX"
                value={calculatedData.tomorrow.rx}
                textColor="text-info"
                valueColor="text-info-dark"
              />
              <StatCard
                label="VIP"
                value={calculatedData.tomorrow.vip}
                textColor="text-info"
                valueColor="text-info-dark"
                style={{ ...smallCardStyle, gridColumn: "span 2" }}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
});

TaskCreator.displayName = "TaskCreator";

export default TaskCreator;
